/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * God-Mode read-only data hook — loads data for a specific org without
 * mutating the global org context. For use by Super Admin inspection only.
 *
 * Riders are loaded via the generic useModuleQuery<Rider>('rider-manager', orgId)
 * hook, demonstrating that the same IDataRepository.getAll path used by tenant
 * pages also serves God Mode — the only difference is the explicit orgId.
 */

import { useState, useEffect } from 'react';
import { Organization, User, Brand } from '../domain/entities';
import { Rider, RiderSettings, WorkflowRequest } from '../modules/rider-ops/types';
import { getRepository } from '../config/repository';
import { hasApiStatus } from '../data/ApiModuleRepository';
import { useModuleQuery } from './useModuleQuery';

export interface GodModeSnapshot {
  org: Organization | null;
  riders: Rider[];
  settings: RiderSettings;
  requests: WorkflowRequest[];
  users: User[];
  brands: Brand[];
  loading: boolean;
}

const defaultSettings: RiderSettings = {
  fileNoStart: 1000,
  fileNoPadding: 4,
  contracts: [],
  cardHeader: 'Rider Card',
};

/**
 * Read-only snapshot of all data for a given orgId.
 * Bypasses the global org context — safe to call while Super Admin is on
 * any page without affecting the rest of the application.
 */
export function useGodModeData(orgId: string): GodModeSnapshot {
  // ── Riders — via generic IDataRepository.getAll with explicit orgId ─────────
  // Same code path as the Rider Manager tenant pages; orgId scopes the results.
  const { data: riders, loading: ridersLoading } =
    useModuleQuery<Rider>('rider-manager', orgId);

  // ── Org, settings, requests, users — via domain-specific *ForOrg reads ──────
  const [org, setOrg]           = useState<Organization | null>(null);
  const [settings, setSettings] = useState<RiderSettings>(defaultSettings);
  const [requests, setRequests] = useState<WorkflowRequest[]>([]);
  const [users, setUsers]       = useState<User[]>([]);
  const [brands, setBrands]     = useState<Brand[]>([]);
  const [otherLoading, setOtherLoading] = useState(true);

  useEffect(() => {
    if (!orgId) return;
    setOtherLoading(true);
    const repo = getRepository();
    void Promise.all([
      repo.getOrganizationById(orgId),
      repo.getSettingsForOrg(orgId),
      repo.getWorkflowRequestsForOrg(orgId),
      repo.getUsersForOrg(orgId),
      repo.getBrands(),
    ]).then(([fetchedOrg, fetchedSettings, fetchedRequests, fetchedUsers, fetchedBrands]) => {
      setOrg(fetchedOrg);
      setSettings(fetchedSettings);
      setRequests(fetchedRequests);
      setUsers(fetchedUsers);
      setBrands(fetchedBrands);
      setOtherLoading(false);
    }).catch((err) => {
      if (!hasApiStatus(err, 401, 403)) {
        console.error(`[useGodModeData] Failed to load snapshot for org ${orgId}`, err);
      }
      setOrg(null);
      setSettings(defaultSettings);
      setRequests([]);
      setUsers([]);
      setBrands([]);
      setOtherLoading(false);
    });
  }, [orgId]);

  return { org, riders, settings, requests, users, brands, loading: ridersLoading || otherLoading };
}
